import { RWSService } from "@rws-framework/client";

import type { User } from "./types";
import type {
  CalendarGrouping,
  Course,
  CoursePermissions,
  Role,
  Section,
  SectionInstructor,
} from "./types";
import { uniqBy } from "src/helpers/utils";
import { inAdminCourseScope } from "src/types/adminCourse";
import { inCoordinatorAccessScope } from "src/types/coordinatorAccess";

/** Manages current user permissions within a course. Used by CourseService. */
class CourseUserService extends RWSService {
  public static DEFAULT_SECTION_ROLE: Role = "instructor_no_edit";

  getUser(): Promise<User> {
    return new Promise((resolve) => {
      document.documentElement.dispatchEvent(
        new CustomEvent("jnct:junctionuser:getuser", {
          detail: resolve,
          bubbles: true,
          composed: true,
          cancelable: true,
        }),
      );
    });
  }

  static getUserPermissionsInCourse(
    user: User,
    course: Course,
    {
      section,
      calendar,
    }: { section?: Section; calendar?: CalendarGrouping } = {},
  ): CoursePermissions {
    const userId = user.mongoId;
    const courseInstructor = course.instructors.find(
      (u) => u.instructor.id === userId,
    );
    const sectionInstructorRoles = (course.calendarGroupings || [])
      .flatMap((g) => g.sections)
      .flatMap((s) => s.instructors)
      .filter((u) => u.instructor.id === userId)
      .map((u) => u.role);

    const calendarInstructorRoles = (calendar?.sections || [])
      .flatMap((s) => s.instructors)
      .filter((u) => u.instructor.id === userId)
      .map((u) => u.role);

    const sectionInstructor = section?.instructors.find(
      (u) => u.instructor.id === userId,
    );

    const sectionRole: Role | undefined =
      (!!section && !!sectionInstructor && sectionInstructor.role) || undefined;
    const courseRole: Role | undefined = courseInstructor?.role;

    const hasAdminCourseAccess =
      user.admin_role_permissions?.course_content === "rw" &&
      inAdminCourseScope(course.id, user.admin_course);

    const hasCoordinatorAccess = inCoordinatorAccessScope(
      course.id,
      user.coordinator_access,
    );

    const hasFullAccess =
      user.program_administrator ||
      hasAdminCourseAccess ||
      hasCoordinatorAccess;

    const canViewCourse =
      hasFullAccess || !!courseRole || !!sectionInstructorRoles.length;
    const canViewCourseMaster = hasFullAccess || !!courseRole;
    const canEditCalendar =
      hasFullAccess ||
      courseRole === "instructor_edit_course" ||
      courseRole === "instructor_edit_calendar" ||
      calendarInstructorRoles.includes("instructor_edit_calendar");
    const canViewSection =
      hasFullAccess ||
      (canViewCourse && !!sectionRole) ||
      courseRole === "instructor_edit_course";
    const canManageCourse =
      hasFullAccess ||
      courseRole === "instructor_edit_course" ||
      courseRole === "instructor_edit_calendar" ||
      sectionInstructorRoles.includes("instructor_edit_calendar");
    const canEditCourse =
      hasFullAccess || courseRole === "instructor_edit_course";
    const canGoToSection =
      hasFullAccess || !!courseInstructor || !!sectionInstructor;

    return {
      canViewCourse,
      canViewCourseMaster,
      canViewSection,
      canManageCourse,
      canEditCalendar,
      canEditCourse,
      canGoToSection,
    };
  }

  /** Mirrors the `make_program` blank-course gate in `section_management/model.py`. */
  static canCreateBlankCourse(user: User) {
    return (
      user.admin_role_permissions.course_admin === "rw"
    );
  }

  static findUserRoleInCalendar(
    userId: string,
    calendar: CalendarGrouping,
  ): Role | null {
    const rolesInSections = calendar.sections
      .flatMap((s) => s.instructors)
      .filter((i) => i.instructor.id === userId)
      .map((i) => i.role);

    if (rolesInSections.length === 0) return null;
    // this scenario should not happen, as roles on sections are limited to `instructor_edit_calendar` and `instructor_no_edit`
    if (rolesInSections.includes("instructor_edit_course"))
      return "instructor_edit_calendar";
    if (rolesInSections.includes("instructor_edit_calendar"))
      return "instructor_edit_calendar";

    return "instructor_no_edit";
  }

  static findInstructorsInCalendar(
    calendar: CalendarGrouping,
  ): SectionInstructor[] {
    const instructors = calendar.sections
      .flatMap((s) => s.instructors)
      .map((i) => i.instructor);

    const uniqInstructors = uniqBy(instructors, (i) => i.id);

    return uniqInstructors.map((instructor) => ({
      instructor,
      role: CourseUserService.findUserRoleInCalendar(instructor.id, calendar),
    }));
  }

  static findUserRoleInCalendarOrDefault(
    userId: string,
    calendar: CalendarGrouping,
  ): Role {
    return (
      CourseUserService.findUserRoleInCalendar(userId, calendar) ||
      CourseUserService.DEFAULT_SECTION_ROLE
    );
  }
}

export default CourseUserService.getSingleton();

export { CourseUserService };
